import { t, TFunction } from 'i18next';
import * as yup from 'yup';

import { BrainGuidelines } from '@/shared/types/brain';

import { BrainTypeValues, ResponseLength } from './models';

export const MAX_FORM_CHARACTERS = 10000;

/** CSS selector for BlockNote heading blocks, used for scroll-spy and click-to-scroll */
export const HEADING_BLOCK_SELECTOR = '.bn-block-content[data-content-type="heading"]';

export const getAIAgentType = (brain_type: BrainTypeValues) => {
  switch (brain_type) {
    case 'activation':
      return t('ai_agents.types.activation.title');
    case 'support':
      return t('ai_agents.types.support.title');
    case 'debt_collection':
      return t('ai_agents.types.debt_collection.title');
    case 'engagement':
      return t('ai_agents.types.engagement.title');
    case 'upsell':
      return t('ai_agents.types.upsell.title');
    case 'sales_fintech':
      return t('ai_agents.types.sales_fintech.title');
    case 'sales_ecommerce':
      return t('ai_agents.types.sales_ecommerce.title');
    default:
      return brain_type;
  }
};

export const getResponseLength = (length: ResponseLength) => {
  switch (length) {
    case 'short':
      return t('ai_agents.response_length.short');
    case 'medium':
      return t('ai_agents.response_length.medium');
    case 'long':
      return t('ai_agents.response_length.long');
  }
};

export const fieldSchema = <T extends Record<string, string>>(
  name: keyof T,
  limit: number,
  t: TFunction
): yup.ObjectSchema<T> => {
  return yup
    .object()
    .shape({
      [name]: yup.string().max(limit, t('validation.max_length', { 0: limit })),
    }) as yup.ObjectSchema<T>;
};

/**
 * Removes trailing <br> tags from a string
 * @param input - The input string that may contain trailing <br> tags
 * @returns The cleaned string with trailing <br> tags removed
 * @example
 * cleanText("Hello<br><br>") // returns "Hello"
 * cleanText("Hello<br> <br> ") // returns "Hello"
 */
export const removeTrailingBRs = (input: string): string => {
  // Remove one or more <br> tags (with optional trailing spaces) at the end of the string
  return input.replace(/(?:<br>\s*|\n|\r)+$/g, '').trimEnd();
};

/**
 * Utility to get the last visited category for a collection (for use outside React hooks)
 */
export function getLastVisitedCategory(id?: string): string {
  if (!id) {
    return 'overview';
  }
  try {
    const key = `aiAgents.lastVisitedCategory.${id}`;
    const savedCategory = localStorage.getItem(key);
    if (savedCategory && ['overview', 'guidelines', 'workflows'].includes(savedCategory)) {
      return savedCategory;
    }
  } catch (error) {
    // Log the error for debugging purposes
    console.error('Failed to retrieve last visited category from localStorage:', error);
  }
  return 'overview';
}

/**
 * Converts objections data to markdown format.
 *
 * @param {BrainGuidelines['objections']} objections - The objections data to convert.
 *   Can be an array of objection objects, a string, or undefined/null.
 * @returns {string} The formatted markdown string. Returns empty string if objections
 *   is falsy or an empty array. For arrays, each objection is formatted as a heading
 *   with the message followed by the response. For strings, returns the string as-is.
 */
export const objectionsToMarkdown = (objections: BrainGuidelines['objections']): string => {
  if (!objections) {
    return '';
  }
  if (Array.isArray(objections) && objections.length > 0) {
    return objections.map((obj) => `## ${obj.message}\n\n${obj.response}`).join('\n\n');
  }
  if (typeof objections === 'string') {
    return objections;
  }
  return '';
};

/**
 * Converts custom guidelines data to markdown format.
 *
 * @param {BrainGuidelines['custom_guidelines']} customGuidelines - The custom guidelines
 *   data to convert. Can be an array of guideline objects, a string, or undefined/null.
 * @returns {string} The formatted markdown string. Returns empty string if customGuidelines
 *   is falsy or an empty array. For arrays, each guideline is formatted as a heading
 *   with the title followed by the body content. For strings, returns the string as-is.
 */
export const customGuidelinesToMarkdown = (
  customGuidelines: BrainGuidelines['custom_guidelines']
): string => {
  if (!customGuidelines) {
    return '';
  }
  if (Array.isArray(customGuidelines) && customGuidelines.length > 0) {
    return customGuidelines
      .map((guideline) => `## ${guideline.title}\n\n${guideline.body}`)
      .join('\n\n');
  }
  if (typeof customGuidelines === 'string') {
    return customGuidelines;
  }
  return '';
};

/**
 * Strips common markdown syntax from text to get plain text representation.
 * This is used to normalize heading text for comparison with rendered DOM textContent.
 *
 * @param {string} text - The text containing markdown syntax
 * @returns {string} Plain text with markdown syntax removed
 *
 * @example
 * stripMarkdownSyntax('**Bold** text'); // Returns: "Bold text"
 * stripMarkdownSyntax('Using `useState` hook'); // Returns: "Using useState hook"
 * stripMarkdownSyntax('[Link](https://example.com)'); // Returns: "Link"
 * @example
 * stripMarkdownSyntax('{{call_back_time}}'); // Returns: "{{call_back_time}}"
 */
const stripMarkdownSyntax = (text: string): string => {
  return (
    text
      .replace(/\*\*([^*]+)\*\*/g, '$1') // **bold** → bold
      .replace(/\*([^*]+)\*/g, '$1') // *italic* → italic
      // _italic_ → italic, but only at a word boundary so snake_case names survive
      .replace(/(^|[^\p{L}\p{N}])_([^_]+)_(?=[^\p{L}\p{N}]|$)/gu, '$1$2')
      .replace(/`([^`]+)`/g, '$1') // `code` → code
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [text](url) → text
      .replace(/~~([^~]+)~~/g, '$1') // ~~strikethrough~~ → strikethrough
  );
};

export type HeadingItem = { text: string; level: 1 | 2 };

/**
 * Extracts level 1 and level 2 headings (# and ##) from markdown content and strips markdown syntax.
 *
 * This function parses markdown text and extracts all headings that start with one or two
 * hash symbols (# or ##), excluding deeper headings (###, ####, etc.). The returned
 * headings have markdown syntax stripped to match what appears in the rendered DOM,
 * enabling proper scroll navigation when users click on headings in the sticky nav.
 *
 * @param {string | undefined} markdown - The markdown content to parse. Can be undefined or empty.
 * @returns {HeadingItem[]} An array of heading objects with plain text and level (1 or 2).
 *   Returns empty array if markdown is undefined, empty, or contains no level 1 or 2 headings.
 *
 * @example
 * const markdown = "# Main Title\n\nSome text\n\n## **Introduction**\n\nMore text";
 * extractHeadingsFromMarkdown(markdown); // Returns: [{ text: "Main Title", level: 1 }, { text: "Introduction", level: 2 }]
 */
export const extractHeadingsFromMarkdown = (markdown: string | undefined): HeadingItem[] => {
  if (!markdown) {
    return [];
  }

  // Match lines that start with # or ## (but not ###)
  // [ \t]+ matches only spaces and tabs (not newlines)
  // [^\n\r]+ ensures we only match characters on the same line
  const headingLine = /^#{1,2}[ \t]+([^\n\r]+)$/;
  const headings: HeadingItem[] = [];
  let insideCodeFence = false;

  for (const line of markdown.split(/\r?\n/)) {
    // A `#` inside a fenced code block is not a heading, and counting one shifts every later entry
    if (/^\s*(```|~~~)/.test(line)) {
      insideCodeFence = !insideCodeFence;
      continue;
    }
    if (insideCodeFence) {
      continue;
    }

    const match = headingLine.exec(line);
    if (!match) {
      continue;
    }
    // Skip if this is a level 3+ heading (matched by accident due to greedy matching)
    if (match[0].startsWith('###')) {
      continue;
    }
    const level: 1 | 2 = match[0].charAt(1) === '#' ? 2 : 1;
    // Strip markdown syntax to match rendered DOM textContent
    const strippedHeading = stripMarkdownSyntax(match[1].trim());
    if (!strippedHeading) {
      continue;
    }
    headings.push({ text: strippedHeading, level });
  }

  return headings;
};

/**
 * Merges custom instructions and custom guidelines into a single markdown string.
 *
 * This function combines custom instructions (markdown) with custom guidelines
 * (converted to markdown) into a unified instruction set. If both parts contain
 * content, they are separated by two newlines for proper formatting.
 *
 * @param {BrainGuidelines['custom_instructions']} customInstructions -
 *   The custom instructions in markdown format. Can be null/undefined.
 * @param {BrainGuidelines['custom_guidelines']} customGuidelines -
 *   The custom guidelines object that will be converted to markdown format.
 *
 * @returns {string} The merged instructions as a single markdown string.
 *   Returns empty string if both inputs are empty/null. If only one input
 *   has content, returns that content without extra separators.
 *
 */
export const getMergedInstructions = (
  customInstructions: BrainGuidelines['custom_instructions'],
  customGuidelines: BrainGuidelines['custom_guidelines']
): string => {
  const customInstructionsMarkdown = customInstructions || '';
  const customGuidelinesMarkdown = customGuidelinesToMarkdown(customGuidelines);

  // Only add separator if both parts have content
  const mergedInstructions =
    customInstructionsMarkdown +
    (customInstructionsMarkdown && customGuidelinesMarkdown ? '\n\n' : '') +
    customGuidelinesMarkdown;

  return mergedInstructions;
};
